import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx } from "react/jsx-runtime";
import React, { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { zIndexScale } from '../../../foundations/zIndex.constants';
import { OverlayContext } from '../../../providers/OverlayContextProvider';
import { generateUniqueId } from '../../../utils/generateUniqueId';
import { useStateInRef } from '../../../utils/useStateInRef';
import { useOverlayHostZIndex } from '../OverlayPopupZIndex/OverlayPopupZIndex';
function PortalHost(param) {
    let { element, onAttached, onHostZIndex } = param;
    const hostZIndex = useOverlayHostZIndex();
    // WHY: layout effect, so the band reaches the portalled children in the same commit the page is attached in — a passive effect leaves a popup that mounts open painting one frame in the wrong band.
    useLayoutEffect(()=>{
        if (hostZIndex != null) {
            onHostZIndex(hostZIndex);
        }
    }, [
        hostZIndex,
        onHostZIndex
    ]);
    const attachElement = (node)=>{
        if (node != null && element.parentElement !== node) {
            node.appendChild(element);
            onAttached();
        }
    };
    return /*#__PURE__*/ _jsx("div", {
        style: {
            display: 'contents'
        },
        ref: attachElement
    });
}
export function useHostInBottomSheet(options) {
    const { enabled } = options;
    const overlay = useContext(OverlayContext);
    const pageId = useMemo(()=>generateUniqueId('surface-sheet-page'), []);
    const portalTarget = useMemo(()=>{
        const element = document.createElement('div');
        element.style.display = 'contents';
        return element;
    }, []);
    const joinedRef = useRef(false);
    const [portalTargetAttached, setPortalTargetAttached] = useState(false);
    const [hostZIndex, setHostZIndex] = useState(zIndexScale.overlay);
    const getArgs = useStateInRef(_object_spread_props(_object_spread({}, options), {
        overlay
    }));
    useEffect(()=>{
        if (!enabled) return undefined;
        const args = getArgs();
        const sheetAlreadyOpen = args.overlay.getHasOpenBottomSheet();
        const addPage = !sheetAlreadyOpen ? args.overlay.showBottomSheet : args.overlay.pushBottomSheetPage;
        joinedRef.current = true;
        addPage(_object_spread_props(_object_spread({}, args.page), {
            id: pageId,
            content: /*#__PURE__*/ _jsx(PortalHost, {
                element: portalTarget,
                onAttached: ()=>setPortalTargetAttached(true),
                onHostZIndex: setHostZIndex
            }),
            onRemoved: ()=>{
                if (!joinedRef.current) return;
                joinedRef.current = false;
                getArgs().onRemovedFromStack();
            }
        }));
        return ()=>{
            setPortalTargetAttached(false);
            if (!joinedRef.current) return;
            joinedRef.current = false;
            if (getArgs().hideSheetOnLeave) {
                getArgs().overlay.hideBottomSheet();
            } else {
                getArgs().overlay.removeBottomSheetPage(pageId);
            }
        };
    }, [
        enabled,
        portalTarget,
        pageId
    ]);
    return enabled && portalTargetAttached ? {
        portalTarget,
        pageId,
        hostZIndex
    } : null;
}
